// BOXX operator console — the two screens an unauthenticated visitor can reach.
//
// LoginScreen carries sign-in AND registration. The Vue build shipped them as
// two routes (/login and /register), but registration is not self-serve: it only
// works with an `?org=<uuid>` invitation link, so a separate route mostly served
// a dead end. One card with a toggle keeps the SSO buttons and the error surface
// in one place.
//
// Auth is cookie-based. Nothing here stores a token: POST /auth/login sets
// httpOnly cookies and the shell re-reads /me through onAuthenticated().

import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { ApiError, http } from "./api";
import { Banner, Field, useAsync, useToast } from "./ui";

// The backend is served under an optional base path; the SSO init endpoint is a
// full page navigation, so it needs the prefix spelled out.
const BASE_PATH = ((window as unknown as { __BASE_PATH__?: string }).__BASE_PATH__ ?? "").replace(
  /\/$/,
  ""
);

/** Fixed-window limiter on the auth routes. The exact remaining TTL comes back
 *  in a `Retry-After` header, which the fetch wrapper does not expose, so this
 *  is the ceiling we quote when the body does not carry the number. */
const RATE_LIMIT_WINDOW_S = 60;

export interface AuthScreenProps {
  onAuthenticated: () => void | Promise<void>;
  navigate: (to: string, replace?: boolean) => void;
}

// ============================================================ payloads

interface SSOProvider {
  provider: string;
  name: string;
}

interface AuthRole {
  id: string;
  name: string;
  description?: string;
  is_system?: boolean;
}

/** `models.User` as returned by login and register (not the /me shape). */
interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  organization_id: string;
  is_active: boolean;
  is_available: boolean;
  is_super_admin: boolean;
  role_id?: string;
  role?: AuthRole | null;
}

interface CookieAuthResponse {
  expires_in: number;
  user: AuthUser;
}

/** The /me subset this file needs: the role decides where SSO lands you. */
interface SessionUser {
  id: string;
  email: string;
  full_name: string;
  role?: { id: string; name: string } | null;
}

interface FormError {
  message: string;
  traceId?: string;
}

// ============================================================ helpers

/** Seconds to wait after a 429, and whether the server told us exactly. */
function retryWindow(err: ApiError): { seconds: number; exact: boolean } {
  const raw = err.details?.retry_after ?? err.details?.retryAfter;
  const n = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : NaN;
  if (Number.isFinite(n) && n > 0) return { seconds: Math.ceil(n), exact: true };
  return { seconds: RATE_LIMIT_WINDOW_S, exact: false };
}

function asFormError(err: unknown, fallback: string): FormError {
  if (err instanceof ApiError) return { message: err.message || fallback, traceId: err.traceId };
  return { message: fallback };
}

// Provider marks, carried over from the Vue screen so the buttons stay
// recognisable at a glance. Anything unlisted falls back to the generic mark.
const PROVIDER_ICON: Record<string, string> = {
  google:
    "M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z",
  microsoft: "M11 11H3V3h8v8zm10 0h-8V3h8v8zM11 21H3v-8h8v8zm10 0h-8v-8h8v8z",
  github:
    "M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z",
  facebook:
    "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
  custom:
    "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z",
};

function BrandHead({ title, sub }: { title: string; sub: string }) {
  return (
    <>
      <div className="brand">
        <span className="mark">BX</span>
        <span className="name">
          BOXX
          <small>Cyber Concierge</small>
        </span>
      </div>
      <h1>{title}</h1>
      <p className="sub">{sub}</p>
    </>
  );
}

// ============================================================ login

type Mode = "signin" | "register";

interface FieldErrors {
  fullName?: string;
  email?: string;
  password?: string;
  confirm?: string;
}

export function LoginScreen({ onAuthenticated, navigate }: AuthScreenProps) {
  const toast = useToast();

  // Read once: the shell's navigate() rewrites the path, and re-reading later
  // would lose the invitation and redirect parameters we landed with.
  const [query] = useState(() => new URLSearchParams(window.location.search));
  const organizationId = useMemo(() => query.get("org") ?? "", [query]);
  const redirectTo = useMemo(() => query.get("redirect") || "/", [query]);

  const [mode, setMode] = useState<Mode>(() =>
    window.location.pathname.replace(/\/+$/, "").endsWith("/register") ? "register" : "signin"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<FormError | null>(null);
  const [invalid, setInvalid] = useState<FieldErrors>({});
  const [cooldown, setCooldown] = useState(0);
  const [cooldownExact, setCooldownExact] = useState(false);
  const [ssoError, setSsoError] = useState("");

  const providers = useAsync<SSOProvider[]>(
    () => http.get<SSOProvider[]>("/auth/sso/providers"),
    []
  );

  // The SSO callback bounces failures back here as ?sso_error=<message>.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const message = params.get("sso_error");
    if (!message) return;
    setSsoError(message);
    params.delete("sso_error");
    // navigate() compares path only and would no-op here, so the query string is
    // rewritten directly — otherwise a reload resurrects a stale failure.
    const rest = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const finish = useCallback(async () => {
    await onAuthenticated();
    navigate(redirectTo, true);
  }, [onAuthenticated, navigate, redirectTo]);

  const fail = useCallback((err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 429) {
      const wait = retryWindow(err);
      setCooldown(wait.seconds);
      setCooldownExact(wait.exact);
    }
    setError(asFormError(err, fallback));
  }, []);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setInvalid({});
    setPassword("");
    setConfirm("");
  };

  async function signIn(e: FormEvent) {
    e.preventDefault();
    const bad: FieldErrors = {};
    if (!email.trim()) bad.email = "Enter your email.";
    if (!password) bad.password = "Enter your password.";
    setInvalid(bad);
    if (Object.keys(bad).length) return;

    setBusy(true);
    setError(null);
    try {
      await http.post<CookieAuthResponse>("/auth/login", { email: email.trim(), password });
      toast.push("Signed in", "good");
      await finish();
    } catch (err) {
      fail(err, "That email and password did not match an account.");
    } finally {
      setBusy(false);
    }
  }

  async function register(e: FormEvent) {
    e.preventDefault();
    const bad: FieldErrors = {};
    if (!fullName.trim()) bad.fullName = "Enter your name.";
    if (!email.trim()) bad.email = "Enter your email.";
    if (!password) bad.password = "Enter a password.";
    else if (password.length < 8) bad.password = "Use at least 8 characters.";
    if (password && confirm !== password) bad.confirm = "The two passwords do not match.";
    setInvalid(bad);
    if (Object.keys(bad).length) return;

    setBusy(true);
    setError(null);
    try {
      await http.post<CookieAuthResponse>("/auth/register", {
        full_name: fullName.trim(),
        email: email.trim(),
        password,
        organization_id: organizationId,
      });
      toast.push("Account created", "good");
      await finish();
    } catch (err) {
      fail(err, "We could not create the account.");
    } finally {
      setBusy(false);
    }
  }

  const startSSO = (provider: string) => {
    // A full page navigation, not a fetch: the provider redirect chain has to
    // run in the browser and come back with the auth cookies set.
    window.location.href = `${BASE_PATH}/api/auth/sso/${provider}/init`;
  };

  const ssoList = providers.data ?? [];
  const registering = mode === "register";
  const invited = Boolean(organizationId);

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <BrandHead
          title={registering ? "Create your account" : "Welcome back"}
          sub={
            registering
              ? "Join your organisation's console."
              : "Sign in to the Cyber Concierge console."
          }
        />

        {ssoError && (
          <Banner tone="critical">
            {ssoError}
          </Banner>
        )}

        {error && (
          <Banner tone={cooldown > 0 ? "warning" : "critical"} traceId={error.traceId}>
            {error.message}
              {cooldown > 0 &&
                ` Try again in ${cooldownExact ? "" : "up to "}${cooldown}s.`}
            
          </Banner>
        )}

        <motion.div
          key={mode}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
        >
          {registering && !invited ? (
            <div className="empty">
              <h3>Invitation required</h3>
              <p>
                Accounts are created from an invitation link that names your organisation. Ask an
                administrator to send you one, then open it in this browser.
              </p>
              <button className="btn" type="button" onClick={() => switchMode("signin")}>
                Back to sign in
              </button>
            </div>
          ) : registering ? (
            <form onSubmit={register} noValidate>
              <Field label="Full name" error={invalid.fullName} required>
                <input
                  id="full_name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jane Doe"
                  autoComplete="name"
                  disabled={busy}
                />
              </Field>
              <Field label="Email" error={invalid.email} required>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  disabled={busy}
                />
              </Field>
              <Field label="Password" hint="At least 8 characters." error={invalid.password} required>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  disabled={busy}
                />
              </Field>
              <Field label="Confirm password" error={invalid.confirm} required>
                <input
                  id="confirm_password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  disabled={busy}
                />
              </Field>
              <button className="btn primary block lg" type="submit" disabled={busy}>
                {busy && <span className="spinner" />}
                Create account
              </button>
            </form>
          ) : (
            <form onSubmit={signIn} noValidate>
              <Field label="Email" error={invalid.email} required>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  disabled={busy}
                />
              </Field>
              <Field label="Password" error={invalid.password} required>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  disabled={busy}
                />
              </Field>
              <button className="btn primary block lg" type="submit" disabled={busy}>
                {busy && <span className="spinner" />}
                Sign in
              </button>
            </form>
          )}
        </motion.div>

        {/* SSO is sign-in only: the provider decides the organisation, so it
            cannot satisfy an invitation-scoped registration. */}
        {!registering && (providers.loading || providers.error || ssoList.length > 0) && (
          <>
            <div className="auth-sep">or continue with</div>
            {providers.loading && (
              <div className="auth-sso">
                <div className="skeleton" />
                <div className="skeleton" />
              </div>
            )}
            {providers.error && (
              <Banner
                tone="warning"
                traceId={providers.error instanceof ApiError ? providers.error.traceId : undefined}
              >
                Single sign-on options could not be loaded. Signing in with a password still
                  works.
                
              </Banner>
            )}
            {!providers.loading && ssoList.length > 0 && (
              <div className="auth-sso">
                {ssoList.map((p) => (
                  <button
                    key={p.provider}
                    className="btn block"
                    type="button"
                    onClick={() => startSSO(p.provider)}
                    disabled={busy}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d={PROVIDER_ICON[p.provider] ?? PROVIDER_ICON.custom} />
                    </svg>
                    Continue with {p.name}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        <div className="auth-alt">
          {registering ? (
            <>
              Already have an account?{" "}
              <button className="link" type="button" onClick={() => switchMode("signin")}>
                Sign in
              </button>
            </>
          ) : (
            <>
              Have an invitation?{" "}
              <button className="link" type="button" onClick={() => switchMode("register")}>
                Create an account
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================ sso callback

type Phase = "loading" | "success" | "error";

export function SSOCallbackScreen({ onAuthenticated, navigate }: AuthScreenProps) {
  const toast = useToast();
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<FormError | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    (async () => {
      try {
        // The provider redirect already set the cookies; /me is how we find out
        // whether they are actually good for anything.
        const me = await http.get<SessionUser>("/me");
        if (cancelled) return;
        setPhase("success");
        toast.push("Signed in", "good");

        // Everyone lands on the dashboard. The agent role used to be sent to
        // /analytics/agents, which measured a human agent's queue and response times and
        // was removed with the live-agent inbox. Left as it was, the one role that could
        // not see the dashboard would have been redirected straight to a Not-found page
        // on every sign-in.
        const target = "/";
        timer = window.setTimeout(async () => {
          await onAuthenticated();
          navigate(target, true);
        }, 1000);
      } catch (err) {
        if (cancelled) return;
        setPhase("error");
        setError(asFormError(err, "Single sign-on did not complete."));
      }
    })();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
    // Deliberately runs once. onAuthenticated and navigate are stable in the
    // shell, and a re-run would restart the redirect timer.
  }, []);

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <BrandHead
          title={
            phase === "loading"
              ? "Finishing sign-in"
              : phase === "success"
                ? "You are signed in"
                : "Sign-in failed"
          }
          sub={
            phase === "loading"
              ? "Checking the session your provider just created."
              : phase === "success"
                ? "Taking you to the console."
                : "Your provider sent you back, but the session did not stick."
          }
        />

        {phase === "error" && error && (
          <Banner tone="critical" traceId={error.traceId}>
            {error.message}
          </Banner>
        )}

        <div className="empty">
          {phase === "loading" && <span className="spinner" />}
          {phase === "success" && (
            <span className="badge good">
              <span className="dot" />
              Session verified
            </span>
          )}
          {phase === "error" && (
            <button className="btn" type="button" onClick={() => navigate("/login", true)}>
              Return to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
